import { WageFrequency } from "@api/shared-runtime/enums";
import type {
  OrganizationWageSetWages,
  WageFieldKey,
} from "@/features/organization-wage-sets/utils/wage-fields";
import {
  TimesheetEntryBaseWageType,
  wageTypeOptions,
} from "./ad-hoc-time-entry-schema";

type RateKeys = { standard: WageFieldKey; overtime: WageFieldKey };

const WAGE_KEYS_BY_TYPE: Record<TimesheetEntryBaseWageType, RateKeys> = {
  [TimesheetEntryBaseWageType.Regular]: {
    standard: "regularWage",
    overtime: "regularOvertimeWage",
  },
  [TimesheetEntryBaseWageType.Weekend]: {
    standard: "weekendWage",
    overtime: "weekendOvertimeWage",
  },
  [TimesheetEntryBaseWageType.OnCall]: {
    standard: "onCallWage",
    overtime: "onCallOvertimeWage",
  },
  [TimesheetEntryBaseWageType.OnCallCallback]: {
    standard: "onCallCallbackWage",
    overtime: "onCallCallbackOvertimeWage",
  },
  [TimesheetEntryBaseWageType.Holiday]: {
    standard: "holidayWage",
    overtime: "holidayOvertimeWage",
  },
};

/** The wage set field that pays this wage type, as approval selects it. */
export function getWageFieldKey({
  wageType,
  isOvertime,
}: {
  wageType: TimesheetEntryBaseWageType;
  isOvertime: boolean;
}): WageFieldKey {
  const keys = WAGE_KEYS_BY_TYPE[wageType];
  return isOvertime ? keys.overtime : keys.standard;
}

function hasWage(wages: OrganizationWageSetWages, key: WageFieldKey): boolean {
  return wages[key] !== null && wages[key] !== undefined;
}

/** Wage types the wage set has a wage for; Regular is always present. */
export function getWageTypeOptionsForOrgWageSet(
  wages: OrganizationWageSetWages,
) {
  return wageTypeOptions.filter((option) =>
    hasWage(wages, WAGE_KEYS_BY_TYPE[option.value].standard),
  );
}

/** Overtime needs an hourly wage set with an overtime wage for that wage type. */
export function orgWageSetAllowsOvertime({
  wages,
  wageType,
}: {
  wages: OrganizationWageSetWages;
  wageType: TimesheetEntryBaseWageType;
}): boolean {
  if (wages.wageFrequencyId !== WageFrequency.Hourly) {
    return false;
  }
  return hasWage(wages, WAGE_KEYS_BY_TYPE[wageType].overtime);
}
